import { appConfig } from '../config/appConfig';
import { applicationInsights } from './applicationInsights';

export interface PerformanceMetric {
  name: string;
  value: number;
  timestamp: Date;
  metadata?: Record<string, any>;
}

export interface ErrorEvent {
  message: string;
  stack?: string;
  component?: string;
  timestamp: Date;
  userId?: string;
  sessionId?: string;
  metadata?: Record<string, any>;
}

export interface UserEvent {
  type: 'page_view' | 'agent_select' | 'message_send' | 'error_encounter' | 'feature_use';
  details: Record<string, any>;
  timestamp: Date;
  userId?: string;
  sessionId?: string;
}

class MonitoringService {
  private metrics: PerformanceMetric[] = [];
  private errors: ErrorEvent[] = [];
  private userEvents: UserEvent[] = [];
  private performanceObserver?: PerformanceObserver;
  private errorHandler?: (event: ErrorEvent) => void;

  constructor() {
    this.initializePerformanceMonitoring();
    this.initializeErrorHandling();
  }

  // Initialize performance monitoring
  private initializePerformanceMonitoring(): void {
    // Track Web Vitals
    if (typeof window !== 'undefined' && 'PerformanceObserver' in window) {
      try {
                 // Observe Core Web Vitals
         this.performanceObserver = new PerformanceObserver((list) => {
           for (const entry of list.getEntries()) {
             // Handle different entry types with proper value extraction
             let value = 0;
             if ('value' in entry) {
               value = (entry as any).value;
             } else if (entry.duration !== undefined) {
               value = entry.duration;
             } else {
               value = entry.startTime;
             }

             this.trackPerformanceMetric({
               name: entry.name,
               value: value,
               timestamp: new Date(),
               metadata: {
                 entryType: entry.entryType,
                 duration: entry.duration,
               }
             });
           }
         });

        // Observe different performance metrics
        if (PerformanceObserver.supportedEntryTypes.includes('largest-contentful-paint')) {
          this.performanceObserver.observe({ entryTypes: ['largest-contentful-paint'] });
        }
        
        if (PerformanceObserver.supportedEntryTypes.includes('first-input')) {
          this.performanceObserver.observe({ entryTypes: ['first-input'] });
        }
        
        if (PerformanceObserver.supportedEntryTypes.includes('layout-shift')) {
          this.performanceObserver.observe({ entryTypes: ['layout-shift'] });
        }

      } catch (error) {
        console.warn('Performance monitoring setup failed:', error);
      }
    }

    // Track page load time
    if (typeof window !== 'undefined') {
      window.addEventListener('load', () => {
        const perfData = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        
        this.trackPerformanceMetric({
          name: 'page_load_time',
          value: perfData.loadEventEnd - perfData.fetchStart,
          timestamp: new Date(),
          metadata: {
            domContentLoaded: perfData.domContentLoadedEventEnd - perfData.fetchStart,
            firstContentfulPaint: this.getFirstContentfulPaint(),
          }
        });
      });
    }
  }

  // Initialize error handling
  private initializeErrorHandling(): void {
    if (typeof window !== 'undefined') {
      // Catch unhandled errors
      window.addEventListener('error', (event) => {
        this.trackError({
          message: event.message,
          stack: event.error?.stack,
          timestamp: new Date(),
          metadata: {
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
          }
        });
      });

      // Catch unhandled promise rejections
      window.addEventListener('unhandledrejection', (event) => {
        this.trackError({
          message: `Unhandled promise rejection: ${event.reason}`,
          stack: event.reason?.stack,
          timestamp: new Date(),
          metadata: {
            type: 'unhandledrejection',
            reason: event.reason,
          }
        });
      });
    }
  }

  // Track performance metrics
  public trackPerformanceMetric(metric: PerformanceMetric): void {
    if (!appConfig.features.analytics) return;

    this.metrics.push(metric);
    
    // Keep only recent metrics to prevent memory issues
    if (this.metrics.length > 1000) {
      this.metrics = this.metrics.slice(-500);
    }

    // Send to Application Insights
    applicationInsights.trackMetric(metric.name, metric.value, {
      metadataJson: metric.metadata ? JSON.stringify(metric.metadata) : undefined,
      source: 'monitoring_service'
    });

    if (appConfig.development.verboseLogging) {
      console.log('📊 Performance metric:', metric);
    }
  }

  // Track errors
  public trackError(error: ErrorEvent): void {
    this.errors.push(error);
    
    // Keep only recent errors
    if (this.errors.length > 100) {
      this.errors = this.errors.slice(-50);
    }

    // Send to Application Insights
    const errorObj = new Error(error.message);
    if (error.stack) {
      errorObj.stack = error.stack;
    }
    
    applicationInsights.trackException(errorObj, {
      component: error.component,
      userId: error.userId,
      sessionId: error.sessionId,
      metadata: error.metadata ? JSON.stringify(error.metadata) : undefined,
      source: 'monitoring_service'
    });

    if (this.errorHandler) {
      this.errorHandler(error);
    }

    console.error('🚨 Error tracked:', error);
  }

  // Track user events
  public trackUserEvent(event: UserEvent): void {
    if (!appConfig.features.analytics) return;

    this.userEvents.push(event);
    
    // Keep only recent events
    if (this.userEvents.length > 500) {
      this.userEvents = this.userEvents.slice(-250);
    }

    // Send to Application Insights
    applicationInsights.trackEvent(`UserEvent_${event.type}`, {
      eventType: event.type,
      userId: event.userId,
      sessionId: event.sessionId,
      details: JSON.stringify(event.details),
      source: 'monitoring_service'
    });

    if (appConfig.development.verboseLogging) {
      console.log('👤 User event:', event);
    }

    // Only send debug beacons for product-related events
    if (event.type.includes('product') || event.type.includes('card')) {
      try {
        // Use the Express API endpoint, not the Static Web App URL
        const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        fetch(`${apiBase}/api/debug/log`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            source: 'react-frontend',
            level: 'info',
            event: `UserEvent_${event.type}`,
            payload: event
          })
        }).catch(() => {});
      } catch {}
    }
  }

  // Track chat-specific metrics
  public trackChatMetric(type: 'message_sent' | 'response_received' | 'typing_start' | 'typing_end', data: any): void {
    const startTime = data.startTime || Date.now();
    const endTime = Date.now();
    const duration = endTime - startTime;
    
    this.trackPerformanceMetric({
      name: `chat_${type}`,
      value: duration,
      timestamp: new Date(),
      metadata: {
        agentId: data.agentId,
        messageLength: data.messageLength,
        isStreaming: data.isStreaming,
        hasError: data.hasError,
      }
    });

    this.trackUserEvent({
      type: 'message_send',
      details: {
        action: type,
        agentId: data.agentId,
        duration: duration,
      },
      timestamp: new Date(),
      userId: data.userId,
      sessionId: data.sessionId,
    });

    // Additional Application Insights tracking for chat events
    applicationInsights.trackEvent(`ChatMetric_${type}`, {
      agentId: data.agentId,
      messageLength: data.messageLength?.toString(),
      isStreaming: data.isStreaming?.toString(),
      hasError: data.hasError?.toString(),
      userId: data.userId,
      sessionId: data.sessionId,
      source: 'chat_monitoring'
    }, {
      duration: duration,
      messageLength: data.messageLength || 0
    });
  }

  // Get performance summary
  public getPerformanceSummary(): {
    avgResponseTime: number;
    errorRate: number;
    totalEvents: number;
    memoryUsage?: number;
  } {
    const chatMetrics = this.metrics.filter(m => m.name.startsWith('chat_'));
    const avgResponseTime = chatMetrics.length > 0 
      ? chatMetrics.reduce((sum, m) => sum + m.value, 0) / chatMetrics.length 
      : 0;

    const errorRate = this.userEvents.length > 0 
      ? (this.errors.length / this.userEvents.length) * 100 
      : 0;

    const memoryUsage = typeof window !== 'undefined' && 'memory' in performance 
      ? (performance as any).memory.usedJSHeapSize 
      : undefined;

    return {
      avgResponseTime,
      errorRate,
      totalEvents: this.userEvents.length,
      memoryUsage,
    };
  }

  // Get recent errors
  public getRecentErrors(limit: number = 10): ErrorEvent[] {
    return this.errors.slice(-limit);
  }

  // Set error handler
  public setErrorHandler(handler: (event: ErrorEvent) => void): void {
    this.errorHandler = handler;
  }

  // Get First Contentful Paint
  private getFirstContentfulPaint(): number | undefined {
    const entries = performance.getEntriesByName('first-contentful-paint');
    return entries.length > 0 ? entries[0].startTime : undefined;
  }

  // Cleanup
  public cleanup(): void {
    if (this.performanceObserver) {
      this.performanceObserver.disconnect();
    }
  }

  // Export data for analysis
  public exportData(): {
    metrics: PerformanceMetric[];
    errors: ErrorEvent[];
    userEvents: UserEvent[];
    summary: {
      avgResponseTime: number;
      errorRate: number;
      totalEvents: number;
      memoryUsage?: number;
    };
  } {
    return {
      metrics: this.metrics,
      errors: this.errors,
      userEvents: this.userEvents,
      summary: this.getPerformanceSummary(),
    };
  }
}

// Export singleton instance
export const monitoring = new MonitoringService();

// React hook for accessing monitoring data
export const useMonitoring = () => {
  return {
    trackPerformanceMetric: (metric: PerformanceMetric) => monitoring.trackPerformanceMetric(metric),
    trackError: (error: ErrorEvent) => monitoring.trackError(error),
    trackUserEvent: (event: UserEvent) => monitoring.trackUserEvent(event),
    trackChatMetric: (type: 'message_sent' | 'response_received' | 'typing_start' | 'typing_end', data: any) => monitoring.trackChatMetric(type, data),
    getPerformanceSummary: () => monitoring.getPerformanceSummary(),
    getRecentErrors: (limit?: number) => monitoring.getRecentErrors(limit),
  };
}; 